/** Browser data shared by authentication, account switching and 401 handling. */
const USER_ID_KEY = 'catch.session.userId'
const SESSION_GENERATION_KEY = 'catch.session.generation'
const LOGOUT_PENDING_KEY = 'catch.session.logoutPending'

function preservesPrivacyChoice(key: string, value: string | null): boolean {
  return (key.includes('consent') && (value === 'accepted' || value === 'declined'))
    || key.startsWith('__ph_opt_in_out_')
}

/** Tokenless account + sign-in generation, including legacy cached-user sessions. */
export function getStoredSessionIdentity(): string | null {
  try {
    const generation = localStorage.getItem(SESSION_GENERATION_KEY)
    const scope = (identity: string): string => generation ? JSON.stringify([identity, generation]) : identity
    const identity = localStorage.getItem(USER_ID_KEY)
    if (identity) return scope(identity)
    const cached = localStorage.getItem('user')
    if (!cached) return null
    try {
      const previous: unknown = JSON.parse(cached)
      if (previous && typeof previous === 'object' && 'id' in previous) return scope(String(previous.id))
    } catch {
      // A malformed cached user still warrants cleanup when authentication ends.
    }
    return scope('legacy')
  } catch {
    return null
  }
}

/** Remove drafts, pairing codes, provider token caches, user data and intents.
 * Keep only privacy choices. After a 401 interceptor has cleared the old
 * account and remembered its destination, the provider may preserve that one
 * session-scoped route; logout and account switching never opt into this.
 */
export function clearSensitiveBrowserState({ preservePendingRoute = false }: { preservePendingRoute?: boolean } = {}): void {
  for (const [getStorage, isSessionStorage] of [[() => window.localStorage, false], [() => window.sessionStorage, true]] as const) {
    try {
      const storage = getStorage()
      const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index))
      for (const key of keys) {
        const pendingDestination = preservePendingRoute && isSessionStorage && key === 'catch_pending_route'
        if (key && key !== LOGOUT_PENDING_KEY && !pendingDestination && !preservesPrivacyChoice(key, storage.getItem(key))) storage.removeItem(key)
      }
    } catch {
      // Blocked storage cannot be read or restored; memory is cleared by auth.
    }
  }
}

export function prepareBrowserForUser(userId: number): void {
  try {
    let previousId = localStorage.getItem(USER_ID_KEY)
    if (!previousId) {
      const previous: unknown = JSON.parse(localStorage.getItem('user') ?? 'null')
      if (previous && typeof previous === 'object' && 'id' in previous) previousId = String(previous.id)
    }
    if (previousId && previousId !== String(userId)) clearSensitiveBrowserState()
    localStorage.setItem(USER_ID_KEY, String(userId))
    // Re-authentication to the same account is still a new session. This
    // random marker is neither an identifier for a person nor a credential.
    localStorage.setItem(SESSION_GENERATION_KEY, crypto.randomUUID())
  } catch {
    clearSensitiveBrowserState()
  }
}

export function setLogoutPending(pending: boolean, everywhere = false): void {
  try {
    if (pending) localStorage.setItem(LOGOUT_PENDING_KEY, everywhere ? 'all' : 'current')
    else localStorage.removeItem(LOGOUT_PENDING_KEY)
  } catch {
    // The in-memory logout error still tells the user the request failed.
  }
}

export function isLogoutPending(): boolean {
  try {
    return localStorage.getItem(LOGOUT_PENDING_KEY) !== null
  } catch {
    return false
  }
}

export function isGlobalLogoutPending(): boolean {
  try {
    return localStorage.getItem(LOGOUT_PENDING_KEY) === 'all'
  } catch {
    return false
  }
}
